import React, { useState, useEffect } from 'react';
import { useUsers, ROLES } from '../../context/UsersContext';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import StyledSelect from '../StyledSelect';

// Add spinner animation
if (typeof document !== 'undefined' && !document.getElementById('spinner-animation')) {
  const style = document.createElement('style');
  style.id = 'spinner-animation';
  style.innerHTML = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default function InviteUserModal({ onClose }) {
  const { inviteUser, currentUser } = useUsers();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState(ROLES.ACCOUNT_MANAGER);
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Validate email
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      setIsSubmitting(false);
      return;
    }

    // Invite user
    const result = await inviteUser({
      email,
      firstName,
      lastName,
      role,
      title,
    }, currentUser.id);

    setIsSubmitting(false);

    if (result) {
      setInviteSent(true);
    } else {
      setError('Failed to create user. Please try again.');
    }
  };

  // Success confirmation screen
  if (inviteSent) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
        }}
        onClick={onClose}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 12,
            padding: 32,
            maxWidth: 500,
            width: '100%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: C.greenLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Icon name="mail-outline" style={{ fontSize: 28, color: C.green }} />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0, marginBottom: 8 }}>
              ✓ Invite Sent
            </h2>
            <p style={{ fontSize: 13, color: C.textMid, margin: 0, lineHeight: 1.6 }}>
              An invitation email has been sent to <strong style={{ color: C.text }}>{email}</strong>
            </p>
          </div>

          <div style={{
            background: C.blueLight,
            border: `1px solid ${C.blue}`,
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6 }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.text }}>{firstName} {lastName}</strong> has been set up as {role === ROLES.SUPER_USER ? 'a Super User' : 'an Account Manager'}.
              </div>
              <div style={{ fontSize: 11, color: C.textLight }}>
                They will receive their temporary password via email and will be prompted to create a new password on first login.
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '12px',
              background: C.accent,
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Invite form
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 28,
          maxWidth: 500,
          width: '100%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 20 }}>
          Invite New User
        </h2>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@company.com"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                First Name *
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                }}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                Last Name *
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: 13,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  background: C.bg,
                  color: C.text,
                }}
                required
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Role *
            </label>
            <StyledSelect
              value={role}
              onValueChange={setRole}
              options={[
                { value: ROLES.ACCOUNT_MANAGER, label: "Account Manager" },
                { value: ROLES.SUPER_USER, label: "Super User" }
              ]}
              placeholder="Select role"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Senior Account Manager"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: 13,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                background: C.bg,
                color: C.text,
              }}
            />
          </div>

          <div style={{
            background: C.blueLight,
            border: `1px solid ${C.blue}`,
            borderRadius: 6,
            padding: 12,
            fontSize: 11,
            color: C.textMid,
          }}>
            A temporary password will be generated. The user will be prompted to change it on first login.
          </div>

          {error && (
            <div style={{
              background: C.redLight,
              border: `1px solid ${C.red}`,
              borderRadius: 6,
              padding: 10,
              fontSize: 12,
              color: C.red,
              fontWeight: 600,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '10px',
                background: 'transparent',
                color: C.textMid,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: '10px',
                background: isSubmitting ? C.borderLight : C.green,
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? (
                <>
                  <div style={{
                    width: 14,
                    height: 14,
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite',
                  }} />
                  Sending...
                </>
              ) : (
                <>
                  <Icon name="mail-outline" style={{ fontSize: 14 }} />
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
